import { toSafeString } from '@shared/utils';
import toFiniteSeq from '@/lib/threadEvents/toFiniteSeq.js';

const shouldPreserveDuplicateIdentityEvent = ({ existingEvent, incomingEvent } = {}) => {
  const existingType = toSafeString(existingEvent?.type).toLowerCase();
  const incomingType = toSafeString(incomingEvent?.type).toLowerCase();

  if (existingType !== 'line' || incomingType !== 'line') {
    return false;
  }

  const existingToolName = toSafeString(existingEvent?.toolName).toLowerCase();
  const incomingToolName = toSafeString(incomingEvent?.toolName).toLowerCase();

  if (!existingToolName || !incomingToolName) {
    return false;
  }

  return existingToolName !== incomingToolName;
};

const dedupeThreadEventsByIdentity = ({ events = [] } = {}) => {
  const normalizedEvents = Array.isArray(events) ? events : [];

  if (normalizedEvents.length < 2) {
    return normalizedEvents;
  }

  const dedupedEvents = [];
  let didDedupeAnyEvents = false;

  for (const event of normalizedEvents) {
    const eventId = toSafeString(event?.id);
    const eventSeq = toFiniteSeq(event?.seq);
    const indexById = eventId
      ? dedupedEvents.findIndex((candidate) => {
          return toSafeString(candidate?.id) === eventId;
        })
      : -1;
    const hasDistinctIdAtSameSeq =
      indexById < 0 &&
      eventId &&
      eventSeq !== null &&
      dedupedEvents.some((candidate) => {
        return toFiniteSeq(candidate?.seq) === eventSeq && toSafeString(candidate?.id) && toSafeString(candidate?.id) !== eventId;
      });
    const indexBySeq =
      indexById >= 0 || eventSeq === null || hasDistinctIdAtSameSeq
        ? -1
        : dedupedEvents.findIndex((candidate) => {
            return toFiniteSeq(candidate?.seq) === eventSeq;
          });
    const targetIndex = indexById >= 0 ? indexById : indexBySeq;

    if (targetIndex < 0) {
      dedupedEvents.push(event);
      continue;
    }

    const existingEvent = dedupedEvents[targetIndex] || {};

    if (
      shouldPreserveDuplicateIdentityEvent({
        existingEvent,
        incomingEvent: event
      })
    ) {
      const dedupeSuffix = dedupedEvents.filter((candidate) => {
        return toSafeString(candidate?.id) === eventId || toSafeString(candidate?.id).startsWith(`${eventId}::`);
      }).length;

      dedupedEvents.push({
        ...event,
        id: eventId ? `${eventId}::${dedupeSuffix}` : event?.id
      });
      continue;
    }

    didDedupeAnyEvents = true;
    const mergedEvent = {
      ...existingEvent,
      ...event
    };

    if (eventId) {
      mergedEvent.id = eventId;
    } else if (toSafeString(existingEvent?.id)) {
      mergedEvent.id = existingEvent.id;
    }

    dedupedEvents[targetIndex] = mergedEvent;
  }

  if (!didDedupeAnyEvents && dedupedEvents.length === normalizedEvents.length) {
    return normalizedEvents;
  }

  return dedupedEvents;
};

export default dedupeThreadEventsByIdentity;
